/**
 * Tutorial Service for managing tutorial status via backend API.
 * 
 * Handles checking tutorial completion status, marking tutorials as completed,
 * and resetting tutorial status. Includes comprehensive error handling and fallback behavior.
 */

import axios, { AxiosError } from 'axios';
import { TutorialServiceInterface } from '../types/tutorial';

/**
 * Tutorial service error types
 */
export enum TutorialErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTHENTICATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Tutorial service error class
 */
export class TutorialServiceError extends Error {
  public readonly type: TutorialErrorType;
  public readonly originalError?: Error;
  public readonly retryable: boolean;
  public readonly statusCode?: number;

  constructor(
    message: string,
    type: TutorialErrorType,
    originalError?: Error,
    retryable: boolean = false,
    statusCode?: number
  ) {
    super(message);
    this.name = 'TutorialServiceError';
    this.type = type;
    this.originalError = originalError;
    this.retryable = retryable;
    this.statusCode = statusCode;
  }
}

/**
 * Tutorial service implementation for API communication.
 */
export class TutorialService implements TutorialServiceInterface {
  private readonly baseUrl: string;
  private readonly timeout: number = 10000; // 10 seconds
  private readonly maxRetries: number = 3;
  private readonly retryDelay: number = 1000; // 1 second

  constructor(baseUrl?: string) {
    // Use environment variable or default to localhost
    this.baseUrl = baseUrl || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
  }

  /**
   * Get tutorial completion status from backend with comprehensive error handling.
   * 
   * @returns Promise<boolean> - True if tutorial is completed, false otherwise
   * @throws TutorialServiceError with specific error type and retry information
   */
  async getTutorialStatus(): Promise<boolean> {
    const url = `${this.baseUrl}/api/tutorial/status`;
    
    try {
      const response = await this.makeRequestWithRetry(async () => {
        return await axios.get(url, {
          timeout: this.timeout,
          headers: this.getAuthHeaders(),
        });
      });

      // Validate response structure
      if (response.data && typeof response.data.tutorial_completed === 'boolean') {
        return response.data.tutorial_completed;
      }

      // Fallback: if response is malformed, assume tutorial not completed
      console.warn('Invalid tutorial status response format, defaulting to false');
      return false;

    } catch (error) {
      const tutorialError = this.handleError(error, 'getTutorialStatus');
      console.error('Failed to get tutorial status:', tutorialError);
      
      // For authentication errors, return false instead of throwing
      // This prevents authentication error messages from showing when user is authenticated
      if (tutorialError.type === TutorialErrorType.AUTHENTICATION_ERROR) {
        console.warn('Authentication error getting tutorial status, defaulting to false (show tutorial)');
        return false;
      }
      
      // For other errors, default to showing tutorial (safer UX)
      // This ensures new users see the tutorial even if there are temporary issues
      return false;
    }
  }

  /**
   * Mark tutorial as completed in backend with comprehensive error handling.
   * 
   * @returns Promise<boolean> - True if successfully marked as completed
   * @throws TutorialServiceError with specific error type and retry information
   */
  async markTutorialCompleted(): Promise<boolean> {
    const url = `${this.baseUrl}/api/tutorial/complete`;
    
    console.log('[TutorialService] markTutorialCompleted called');
    console.log('[TutorialService] Request URL:', url);
    console.log('[TutorialService] Auth headers:', this.getAuthHeaders());
    
    try {
      console.log('[TutorialService] Making POST request to mark tutorial complete...');
      const response = await this.makeRequestWithRetry(async () => {
        return await axios.post(url, {}, {
          timeout: this.timeout,
          headers: this.getAuthHeaders(),
        });
      });

      console.log('[TutorialService] Response received:', response.data);

      // Validate response structure
      if (response.data && response.data.tutorial_completed === true) {
        console.log('[TutorialService] ✓ Successfully marked tutorial as completed in backend');
        console.log('[TutorialService] Response details:', {
          tutorial_completed: response.data.tutorial_completed,
          user_id: response.data.user_id,
          message: response.data.message
        });
        return true;
      }

      console.warn('[TutorialService] ✗ Unexpected response when marking tutorial completed:', response.data);
      return false;

    } catch (error) {
      const tutorialError = this.handleError(error, 'markTutorialCompleted');
      console.error('[TutorialService] ✗ Failed to mark tutorial completed:', tutorialError);
      console.error('[TutorialService] Error details:', {
        type: tutorialError.type,
        message: tutorialError.message,
        statusCode: tutorialError.statusCode,
        retryable: tutorialError.retryable
      });
      
      // For authentication errors, return false instead of throwing
      // This prevents blocking the tutorial completion flow
      if (tutorialError.type === TutorialErrorType.AUTHENTICATION_ERROR) {
        console.warn('[TutorialService] Authentication error marking tutorial completed, tutorial will complete locally only');
        return false;
      }
      
      // For other errors, return false to indicate failure
      return false;
    }
  }

  /**
   * Reset tutorial status in backend with comprehensive error handling.
   * 
   * @returns Promise<boolean> - True if successfully reset
   * @throws TutorialServiceError with specific error type and retry information
   */
  async resetTutorialStatus(): Promise<boolean> {
    const url = `${this.baseUrl}/api/tutorial/reset`;
    
    try {
      const response = await this.makeRequestWithRetry(async () => {
        return await axios.post(url, {}, {
          timeout: this.timeout,
          headers: this.getAuthHeaders(),
        });
      });

      // Validate response structure
      if (response.data && response.data.tutorial_completed === false) {
        console.log('[TutorialService] Successfully reset tutorial status in backend');
        return true;
      }

      console.warn('Unexpected response when resetting tutorial status');
      return false;

    } catch (error) {
      const tutorialError = this.handleError(error, 'resetTutorialStatus');
      console.error('Failed to reset tutorial status:', tutorialError);
      
      // For authentication errors, return false instead of throwing
      // This prevents blocking the tutorial reset flow
      if (tutorialError.type === TutorialErrorType.AUTHENTICATION_ERROR) {
        console.warn('Authentication error resetting tutorial status, tutorial will reset locally only');
        return false;
      }
      
      // For other errors, return false to indicate failure
      return false;
    }
  }

  /**
   * Get authentication headers for API requests with enhanced error handling.
   * 
   * @returns Object with authorization headers
   * @throws TutorialServiceError if no authentication token found
   */
  private getAuthHeaders(): Record<string, string> {
    // Get token from localStorage using the correct key used by AuthService
    const tokenData = localStorage.getItem('textile_vision_tokens');
    
    if (!tokenData) {
      throw new TutorialServiceError(
        'No authentication token found',
        TutorialErrorType.AUTHENTICATION_ERROR,
        undefined,
        false
      );
    }

    try {
      const tokens = JSON.parse(tokenData);
      const accessToken = tokens.accessToken;
      
      if (!accessToken) {
        throw new TutorialServiceError(
          'Access token not found in stored tokens',
          TutorialErrorType.AUTHENTICATION_ERROR,
          undefined,
          false
        );
      }

      return {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };
    } catch (parseError) {
      throw new TutorialServiceError(
        'Invalid token data format',
        TutorialErrorType.AUTHENTICATION_ERROR,
        parseError instanceof Error ? parseError : new Error(String(parseError)),
        false
      );
    }
  }

  /**
   * Make HTTP request with retry logic and comprehensive error handling.
   * 
   * @param requestFn - Function that makes the HTTP request
   * @returns Promise with response data
   * @throws TutorialServiceError with specific error type and retry information
   */
  private async makeRequestWithRetry<T>(requestFn: () => Promise<T>): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error as Error;
        const tutorialError = this.handleError(error, `attempt ${attempt}`);
        
        // Don't retry authentication errors
        if (tutorialError.type === TutorialErrorType.AUTHENTICATION_ERROR) {
          throw tutorialError;
        }

        // Don't retry client errors (4xx except 401)
        if (tutorialError.type === TutorialErrorType.VALIDATION_ERROR) {
          throw tutorialError;
        }

        // If this is the last attempt, throw the error
        if (attempt === this.maxRetries) {
          throw tutorialError;
        }

        // Wait before retrying with exponential backoff
        const delay = this.retryDelay * Math.pow(2, attempt - 1);
        await this.delay(delay);
        console.log(`Retrying tutorial API request (attempt ${attempt + 1}/${this.maxRetries}) after ${delay}ms`);
      }
    }

    // This should never be reached, but TypeScript requires it
    throw this.handleError(lastError || new Error('Request failed after retries'), 'final attempt');
  }

  /**
   * Handle and categorize errors with comprehensive error information.
   * 
   * @param error - Error to handle
   * @param context - Context where error occurred
   * @returns TutorialServiceError with categorized error information
   */
  private handleError(error: unknown, context: string): TutorialServiceError {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const message = (axiosError.response?.data as any)?.message || (axiosError as any).message;

      // Categorize by HTTP status code
      if (status === 401) {
        return new TutorialServiceError(
          'Authentication required',
          TutorialErrorType.AUTHENTICATION_ERROR,
          error,
          false,
          status
        );
      } else if (status && status >= 400 && status < 500) {
        return new TutorialServiceError(
          `Client error: ${message}`,
          TutorialErrorType.VALIDATION_ERROR,
          error,
          false,
          status
        );
      } else if (status && status >= 500) {
        return new TutorialServiceError(
          `Server error: ${message}`,
          TutorialErrorType.SERVER_ERROR,
          error,
          true,
          status
        );
      } else if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'ETIMEDOUT') {
        return new TutorialServiceError(
          'Request timeout',
          TutorialErrorType.TIMEOUT_ERROR,
          error,
          true
        );
      } else if (axiosError.code === 'ERR_NETWORK' || !navigator.onLine) {
        return new TutorialServiceError(
          'Network error',
          TutorialErrorType.NETWORK_ERROR,
          error,
          true
        );
      }
    }

    // Handle non-axios errors
    if (error instanceof TutorialServiceError) {
      return error;
    }

    // Default to unknown error
    return new TutorialServiceError(
      `Unknown error in ${context}: ${error instanceof Error ? error.message : String(error)}`,
      TutorialErrorType.UNKNOWN_ERROR,
      error instanceof Error ? error : new Error(String(error)),
      true
    );
  }

  /**
   * Delay execution for specified milliseconds.
   * 
   * @param ms - Milliseconds to delay
   * @returns Promise that resolves after delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if tutorial service is available (health check) with enhanced error handling.
   * 
   * @returns Promise<boolean> - True if service is available
   */
  async isServiceAvailable(): Promise<boolean> {
    try {
      const response = await axios.get(`${this.baseUrl}/health`, {
        timeout: 5000, // Shorter timeout for health check
      });
      
      return response.status === 200;
    } catch (error) {
      const tutorialError = this.handleError(error, 'health check');
      console.warn('Tutorial service health check failed:', tutorialError);
      return false;
    }
  }

  /**
   * Get service configuration for debugging.
   * 
   * @returns Object with service configuration
   */
  getConfig(): Record<string, unknown> {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      maxRetries: this.maxRetries,
      retryDelay: this.retryDelay,
    };
  }

  /**
   * Get error statistics for monitoring.
   * 
   * @returns Object with error statistics
   */
  getErrorStats(): Record<string, number> {
    // This could be enhanced to track error counts by type
    return {
      totalRequests: 0, // Would need to implement request counting
      totalErrors: 0,   // Would need to implement error counting
      lastErrorTime: 0, // Would need to implement error tracking
    };
  }

  /**
   * Test connection to tutorial service with comprehensive error reporting.
   * 
   * @returns Promise with connection test results
   */
  async testConnection(): Promise<{
    success: boolean;
    error?: TutorialServiceError;
    responseTime?: number;
    serviceInfo?: any;
  }> {
    const startTime = Date.now();
    
    try {
      const isAvailable = await this.isServiceAvailable();
      const responseTime = Date.now() - startTime;
      
      return {
        success: isAvailable,
        responseTime,
        serviceInfo: {
          baseUrl: this.baseUrl,
          timeout: this.timeout,
        },
      };
    } catch (error) {
      const tutorialError = this.handleError(error, 'connection test');
      const responseTime = Date.now() - startTime;
      
      return {
        success: false,
        error: tutorialError,
        responseTime,
      };
    }
  }
}

/**
 * Default tutorial service instance.
 * 
 * Can be imported and used directly, or a new instance can be created
 * with custom configuration.
 */
export const tutorialService = new TutorialService();